import { Injectable } from '@angular/core';
import { sustainabilityActions } from '../actions/sustainability.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';
import { StoreState } from '../store.state';
import { sustainabilitySelectors } from '../selectors/sustainability.selectors';
import { CarbonTrackMapper } from '@core/domain/mappers/sustainability/carbon-track.mapper';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { CarbonTrackFiltersMapper } from '@core/domain/mappers/sustainability/carbon-track-filters.mapper';
import { OriginWarrantyMapper } from '@core/domain/mappers/sustainability/origin-warranty.mapper';
import { ApiSustainabilityService } from '@core/api/services/sustainability/sustainability.service';
import { DownloadIndicatorService } from '@shared/components/download-indicator/download-indicator.service';
import { TranslateService } from '@ngx-translate/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CarbonTrack } from '@core/domain/model/sustainability/carbon-track.model';
import { LoadingIds } from '@shared/constants/loading';
import { withLatestFrom } from 'rxjs/operators';
import { masterSelectors } from '@store/selectors/master.selectors';

@Injectable()
export class SustainabilityEffects {
  fetchCarbonTrack$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(sustainabilityActions.fetchCarbonTrack),
      switchMap(() => {
        const filters = this._store.selectSignal(sustainabilitySelectors.carbonTrackFilters)();
        if (filters == null) {
          return EMPTY;
        }
        return this._apiRequest.fetchCarbonTrack(filters).pipe(
          map(CarbonTrackMapper.requestFromApiToDomain),
          map((value) => value.data),
          map((data) => sustainabilityActions.setCarbonTrackData({ data })),
          catchError((error) => of(sustainabilityActions.setCarbonTrackError({ error: errorFromHttpErrorResponse(error) })))
        );
      })
    );
  });

  downloadCarbonTrackPdf$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(sustainabilityActions.downloadCarbonTrackPdf),
        tap(() => {
          const filters = this._store.selectSignal(sustainabilitySelectors.carbonTrackFilters)();
          const request = CarbonTrackFiltersMapper.filtersToRequestModel(filters!);
          const pdfName = this._translate.instant(`SUSTAINABILITY.CARBON_TRACK.PDF_NAME`);
          this._downloadService.download(this._apiRequest.downloadCarbonTrackPdf(request), pdfName);
        })
      );
    },
    { dispatch: false }
  );

  fetchOriginWarranty$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(sustainabilityActions.fetchOriginWarranty),
      withLatestFrom(
        this._store.select(sustainabilitySelectors.originWarrantyFilters),
        this._store.select(masterSelectors.societies),
        this._store.select(masterSelectors.installations)
      ),
      switchMap(([, filters, societies, installations]) => {
        if (filters == null) {
          return EMPTY;
        }
        return this._apiRequest.fetchOriginWarranty(OriginWarrantyMapper.requestFromDomainToApi(filters)).pipe(
          map((value) => OriginWarrantyMapper.requestFromApiToDomain(value, societies ?? [], installations ?? [])),
          map((value) => value.data),
          map((data) => sustainabilityActions.setOriginWarrantyData({ data })),
          catchError((error) => of(sustainabilityActions.setOriginWarrantyError({ error: errorFromHttpErrorResponse(error) })))
        );
      })
    );
  });

  downloadOriginWarrantyPdf$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(sustainabilityActions.downloadOriginWarrantyPdf),
        withLatestFrom(this._store.select(sustainabilitySelectors.originWarrantyFilters)),
        tap(([, filters]) => {
          if (!filters) {
            return;
          }
          const request = OriginWarrantyMapper.requestFromDomainToApi(filters);
          const nameTranslate = this._translate.instant(`SUSTAINABILITY.WARRANTY_OF_ORIGIN.PDF_NAME`);
          const pdfName = `${nameTranslate}${filters!.cups?.length === 1 ? '_' + filters!.cups[0].cups : ''}`;
          this._downloadService.download(this._apiRequest.downloadOriginWarrantyPdf(request), pdfName);
        })
      );
    },
    { dispatch: false }
  );

  // SUSTAINABILITY-SUMMARY
  /**
   * fetchSummaryCarbonTrack effect
   * fetch data and stores it
   */
  fetchSummaryCarbonTrack$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(sustainabilityActions.fetchSummaryCarbonTrack),
      switchMap(() => this._checkSummaryCarbonTrackData().pipe(map((data) => sustainabilityActions.setSummaryCarbonTrackData({ data }))))
    );
  });

  constructor(
    private readonly _actions$: Actions,
    private readonly _apiRequest: ApiSustainabilityService,
    private readonly _store: Store<StoreState>,
    private readonly _downloadService: DownloadIndicatorService,
    private readonly _translate: TranslateService
  ) {}

  /**
   * _checkSummaryCarbonTrackData
   * Check if there is sored data, else fetch new data
   *
   * @returns Observable<CarbonTrack | null>
   */
  protected _checkSummaryCarbonTrackData(): Observable<CarbonTrack | null> {
    const storedData = this._store.selectSignal(sustainabilitySelectors.summaryCarbonTrackData);
    return storedData() ? toObservable(storedData) : this._fetchSummaryCarbonTrackData();
  }

  /**
   * _fetchSummaryCarbonTrackData
   * Fetch new data if filters exists.
   *
   * @returns Observable<CarbonTrack>
   */
  protected _fetchSummaryCarbonTrackData(): Observable<CarbonTrack | null> {
    const filters = this._store.selectSignal(sustainabilitySelectors.summaryCarbonTrackFilters)();
    if (filters == null) {
      return EMPTY;
    }
    return this._apiRequest.fetchCarbonTrack(filters, LoadingIds.SUSTAINABILITY_SUMMARY).pipe(
      map(CarbonTrackMapper.requestFromApiToDomain),
      map((value) => value.data),
      catchError(() => of(null))
    );
  }
}
